import { useState, useEffect } from 'react';
import { Box, IconButton, TextInput } from '@primer/react';
import { ClockCounterClockwise, X } from 'phosphor-react';
import { Modal } from '@/components/Elements';
import { useGetWorkflowCheckpoints } from '../../api/getWorkflowCheckpoints';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CommitsTimeline from '../CommitsTimeline';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

dayjs.extend(relativeTime);

interface ChangeHistoryModalProps {
  isOpen: boolean;
  isClose: () => void;
  workflowName: string;
  selectedApiFileId: string;
  selectedNormalFileId: string;
  selectedWorkflowId: string;
}

export const ChangeHistoryModal = ({
  isOpen,
  isClose,
  workflowName,
  selectedApiFileId,
  selectedNormalFileId,
  selectedWorkflowId,
}: ChangeHistoryModalProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: any }>();

  // Get normal workflow path by removing _api from the path

  const {
    data: checkpoints,
    isLoading: isLoadingCheckpoints,
    isRefetching,
    refetch: refetchCheckpoints,
  } = useGetWorkflowCheckpoints(projectId, selectedWorkflowId, { enabled: isOpen });

  useEffect(() => {
    if (isOpen) {
      refetchCheckpoints();
    }
  }, [isOpen, projectId, selectedWorkflowId, queryClient, refetchCheckpoints]);

  const isLoading = isLoadingCheckpoints || isRefetching;

  function CustomHeader() {
    return (
      <Box
        sx={{ borderBottom: '1px solid #636C76' }}
        className="p-4 flex items-center justify-between"
      >
        <Box className="flex items-center gap-2">
          <ClockCounterClockwise size={24} color="#FBBF24" />
          <h3 className="text-base font-semibold">
            Change History - {workflowName.split('/').pop()}
          </h3>
        </Box>
        <IconButton
          aria-label="Close"
          icon={X}
          onClick={isClose}
          variant="invisible"
          sx={{ fontSize: '18px' }}
        />
      </Box>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={isClose}
      customHeader={CustomHeader}
      sx={{ width: '60%' }}
      showButtons={false}
    >
      <Box className="overflow-y-hidden max-h-[500px]">
        <p className="text-base font-normal leading-6">History of changes in this file</p>

        <TextInput
          block
          placeholder="Search changes..."
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3, position: 'sticky', top: 0, zIndex: 1, mt: 3 }}
        />

        <CommitsTimeline
          checkpoints={checkpoints}
          isLoading={isLoading}
          searchTerm={searchTerm}
          workflowName={workflowName}
          workflow_id={selectedWorkflowId}
          projectId={projectId}
          selectedApiFileId={selectedApiFileId}
          selectedNormalFileId={selectedNormalFileId}
        />
      </Box>
    </Modal>
  );
};
